import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import projectService from "./project.service";
import {
  CreateProjectAccessDto,
  DeleteProjectAccessDto,
  FullProjectDto,
  ProjectAccessDto,
  ProjectDto,
  ProjectInfoDto,
  RemoveTaskFromProjectDto,
  UpdateProjectAccessDto,
  UpdateProjectDto,
  UpdatedColumnInProjectDto,
  UpdatedNoteInProjectDto,
  UpdatedNoteInProjectReturnDto,
  CreateProjectMilestoneDto,
  UpdateProjectMilestoneDto,
  DeleteProjectMilestoneDto,
  ProjectMilestoneAnswerDto,
  UpdateProjectStatusDto,
  ProjectStatusAnswerDto,
} from "./project.types";
import {
  CollectionDto,
  TaskInCollectionDto,
  UpdateCollectionDto,
} from "../collection/collection.types";
import { createThunkWithError } from "../../featureHelpers/errorParser";
import { TaskDto } from "../types";

export const createQuickProject = createAsyncThunk<
  ProjectInfoDto,
  void,
  { state: RootState }
>("tasks/createQuickProject", async (_, thunkAPI) => {
  try {
    return await projectService.createQuickProject();
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const createCollectionInProject = createAsyncThunk<
  CollectionDto,
  string,
  { state: RootState }
>("tasks/createCollectionInProject", async (projectId, thunkAPI) => {
  try {
    return await projectService.createCollectionInProject(projectId);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

interface DeleteCollectionInProjectDto {
  projectId: string;
  collectionId: string;
}

export const deleteCollectionInProject = createAsyncThunk<
  string,
  DeleteCollectionInProjectDto,
  { state: RootState }
>(
  "tasks/deleteCollectionInProject",
  async ({ projectId, collectionId }, thunkAPI) => {
    try {
      return await projectService.deleteCollectionInProject(
        projectId,
        collectionId,
      );
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const addTaskToProject = createAsyncThunk<
  { task: TaskInCollectionDto; projectId: string },
  { taskId: string; projectId: string },
  { state: RootState }
>("tasks/addTaskToProject", async ({ taskId, projectId }, thunkAPI) => {
  try {
    return {
      task: await projectService.addTaskToProject(taskId, projectId),
      projectId,
    };
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const removeTaskFromProject = createAsyncThunk<
  RemoveTaskFromProjectDto,
  RemoveTaskFromProjectDto,
  { state: RootState }
>(
  "tasks/removeTaskFromProject",
  async ({ taskId, collectionId, projectId }, thunkAPI) => {
    try {
      return await projectService.removeTaskFromProject(
        taskId,
        collectionId,
        projectId,
      );
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const getProjects = createAsyncThunk<
  ProjectInfoDto[],
  { groupId?: string; organizationId?: string },
  { state: RootState }
>("tasks/getProjects", async ({ groupId, organizationId }, thunkAPI) => {
  try {
    return await projectService.getProjects(groupId, organizationId);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

/**
 * Get a specific project by ID
 */
export const getProject = createAsyncThunk<
  FullProjectDto,
  string,
  { state: RootState }
>("tasks/getProject", async (projectId, thunkAPI) => {
  try {
    return await projectService.getProject(projectId);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteProject = createAsyncThunk<
  string,
  string,
  { state: RootState }
>("tasks/deleteProject", async (projectId, thunkAPI) => {
  try {
    return await projectService.deleteProject(projectId);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateProject = createAsyncThunk<
  ProjectDto,
  UpdateProjectDto,
  { state: RootState }
>("tasks/updateProject", async (updateProjectData, thunkAPI) => {
  try {
    return await projectService.updateProject(updateProjectData);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateCollectionInProject = createAsyncThunk<
  CollectionDto,
  { projectId: string; collection: UpdateCollectionDto },
  { state: RootState }
>(
  "tasks/updateCollectionInProject",
  async ({ projectId, collection }, thunkAPI) => {
    try {
      return await projectService.updateCollectionInProject(
        projectId,
        collection,
      );
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const updateColumnPositionsInProject = createAsyncThunk<
  { updatedColumns: UpdatedColumnInProjectDto[]; projectId: string },
  { updatedColumns: UpdatedColumnInProjectDto[]; projectId: string },
  { state: RootState }
>(
  "tasks/updateColumnPositionsInProject",
  async ({ updatedColumns, projectId }, thunkAPI) => {
    try {
      return {
        updatedColumns: await projectService.updateColumnPositionsInProject(
          updatedColumns,
          projectId,
        ),
        projectId: projectId,
      };
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const createProjectAccess = createAsyncThunk<
  ProjectAccessDto,
  CreateProjectAccessDto,
  { state: RootState }
>("tasks/createProjectAccess", async (createTaskAccessData, thunkAPI) => {
  try {
    const res = await projectService.createProjectAccess(createTaskAccessData);
    return res;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteProjectAccess = createAsyncThunk<
  DeleteProjectAccessDto,
  DeleteProjectAccessDto,
  { state: RootState }
>("tasks/deleteProjectAccess", async (deleteProjectAccessDto, thunkAPI) => {
  try {
    const res = await projectService.deleteProjectAccess(
      deleteProjectAccessDto,
    );
    return res;
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateNotePositionsInProject = createAsyncThunk<
  { updatedNotes: UpdatedNoteInProjectReturnDto[]; projectId: string },
  { updatedNotes: UpdatedNoteInProjectDto[]; projectId: string },
  { state: RootState }
>(
  "tasks/updateNotePositionsInProject",
  async ({ updatedNotes, projectId }, thunkAPI) => {
    try {
      return {
        updatedNotes: await projectService.updateNotePositionsInProject(
          updatedNotes,
          projectId,
        ),
        projectId: projectId,
      };
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const getProjectTasks = createThunkWithError<TaskDto[], void>(
  "tasks/getProjectTasks",
  async () => {
    return await projectService.getProjectTasks();
  },
);

export const updateProjectStatus = createThunkWithError<
  ProjectStatusAnswerDto,
  UpdateProjectStatusDto
>("tasks/updateProjectStatus", async (dto) => {
  return await projectService.updateProjectStatus(dto);
});

export const createProjectMilestone = createThunkWithError<
  ProjectMilestoneAnswerDto,
  CreateProjectMilestoneDto
>("tasks/createProjectMilestone", async (dto) => {
  return await projectService.createProjectMilestone(dto);
});

export const updateProjectMilestone = createThunkWithError<
  ProjectMilestoneAnswerDto,
  UpdateProjectMilestoneDto
>("tasks/updateProjectMilestone", async (dto) => {
  return await projectService.updateProjectMilestone(dto);
});

export const deleteProjectMilestone = createThunkWithError<
  DeleteProjectMilestoneDto,
  DeleteProjectMilestoneDto
>("tasks/deleteProjectMilestone", async (dto) => {
  return await projectService.deleteProjectMilestone(dto);
});

export const updateProjectAccess = createThunkWithError<
  ProjectAccessDto,
  UpdateProjectAccessDto
>("tasks/updateProjectAccess", async (dto) => {
  return await projectService.updateProjectAcccess(dto);
});
